import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useShowAllDealers } from 'contexts/ShowAllDealersContext';
import { useTableSelector } from 'hooks/useTableSelector';

type DealerAnimationContextType = {
  isAnimating: boolean;
  dealer: number;
  setIsAnimating: (animating: boolean) => void;
  setDealer: (newDealer: number) => void;
};

export const DealerAnimationContext = createContext<DealerAnimationContextType>({
  isAnimating: false,
  dealer: 0,
  setIsAnimating: () => {},
  setDealer: () => {}
});

export const DealerAnimationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const { dealer: gameDealer } = useTableSelector((table) => table.game);
  // Demo-only "All dealers" mode suppresses the animated floating button, so
  // there is no hand-off to advance this value — track the live seat directly.
  const showAllDealers = useShowAllDealers();
  const [dealer, setDealer] = useState(0);

  // Sync rule (rewritten to fix the "dealer stuck on old seat" bug):
  //
  //   • While `isAnimating` is true, hold the previous seat. The floating
  //     `DealerButton` is mid-flight to the new seat; the per-seat DealerChip
  //     hides during animation (its render checks `!isAnimating`), and
  //     `DealerButton` will call setDealer(newSeat) from its timeline
  //     onComplete to hand the chip back to the correct seat.
  //
  //   • Whenever `isAnimating` is false, sync to the server value. This
  //     covers three cases:
  //       1. First mount / initial hand — dealer was 0, now adopt gameDealer.
  //       2. Animation completed normally — DealerButton flipped isAnimating
  //          false; we now converge to gameDealer.
  //       3. Animation NEVER completed — the timeline was interrupted (seat
  //          refs re-arranged mid-flight, orientation flipped, a rapid dealer
  //          change replaced the target, etc.) and `onComplete` was never
  //          called. Previously the context locked at the pre-animation seat
  //          forever (`if (prevDealer > 0) return prevDealer`) and the React
  //          dealer chip lagged the backend — the reported "button stuck on
  //          Aaric_G while Unity shows Hermione_G" symptom. Now that
  //          `isAnimating` is a dep, the moment it flips back to false the
  //          sync catches up regardless of whether onComplete fired.
  //
  // showAllDealers stays a dep so demo mode also re-syncs when toggled.
  useEffect(() => {
    if (isAnimating) return;
    setDealer(+gameDealer);
  }, [gameDealer, isAnimating, showAllDealers]);

  // PERF: memoized so consumers only re-render when one of these values
  // actually changes, not on every provider re-render. See
  // docs/investigations/20260819-game-client-overheating.md.
  const value = useMemo(
    () => ({ isAnimating, dealer, setIsAnimating, setDealer }),
    [isAnimating, dealer]
  );

  return (
    <DealerAnimationContext.Provider value={value}>{children}</DealerAnimationContext.Provider>
  );
};
